import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, X, ExternalLink, Loader2, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AdminQuest() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [filter, setFilter] = useState("pending");
    const [list, setList] = useState([]);
    const [busyId, setBusyId] = useState(null);
    const [rejectingId, setRejectingId] = useState(null);
    const [rejectReason, setRejectReason] = useState("");

    useEffect(() => {
        if (!user) return;
        if (user.role !== "admin") { nav("/"); return; }
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, filter]);

    const load = async () => {
        try {
            const { data } = await api.get(`/admin/quest/submissions${filter ? `?status=${filter}` : ""}`);
            setList(data);
        } catch { setList([]); }
    };

    const approve = async (id) => {
        setBusyId(id);
        try {
            await api.post(`/admin/quest/${id}/review`, { status: "approved", rejection_reason: "" });
            toast.success("Submission approvata");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
        finally { setBusyId(null); }
    };

    const reject = async (id) => {
        setBusyId(id);
        try {
            await api.post(`/admin/quest/${id}/review`, { status: "rejected", rejection_reason: rejectReason || "Attività non valida" });
            toast.success("Submission respinta");
            setRejectingId(null); setRejectReason("");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
        finally { setBusyId(null); }
    };

    if (!user) return <div className="max-w-3xl mx-auto px-5 py-20 text-zinc-400">Caricamento…</div>;

    return (
        <div data-testid="admin-quest-page" className="max-w-[1100px] mx-auto px-5 md:px-10 py-14">
            <Link to="/admin" className="text-xs uppercase tracking-[0.3em] text-zinc-400 hover:text-abnormal-red inline-flex items-center gap-2">
                <ArrowLeft size={14} /> Admin
            </Link>
            <div className="mt-6 flex items-center gap-3">
                <Trophy className="text-abnormal-red" />
                <h1 className="font-display text-5xl md:text-7xl uppercase leading-none">Quest</h1>
            </div>
            <p className="text-zinc-400 mt-3 max-w-2xl">Valida le submission delle sfide (Strava URL). Una volta approvata, scatta l'email reward.</p>

            <div className="mt-6 flex gap-2">
                {["pending", "approved", "rejected", ""].map((f) => (
                    <button
                        key={f || "all"}
                        onClick={() => setFilter(f)}
                        className={`text-[10px] uppercase tracking-[0.25em] font-bold border px-3 py-1.5 transition-colors ${filter === f ? "bg-abnormal-red text-white border-abnormal-red" : "border-white/20 text-zinc-300 hover:border-white"}`}
                    >
                        {f || "tutte"}
                    </button>
                ))}
            </div>

            <div className="mt-8 border border-white/10 divide-y divide-white/10">
                {list.length === 0 ? (
                    <p className="px-5 py-10 text-zinc-500 text-sm">Nessuna submission.</p>
                ) : list.map((s) => (
                    <div key={s.id} className="p-5 grid md:grid-cols-[1fr_auto] gap-4 items-center">
                        <div className="min-w-0">
                            <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">
                                {new Date(s.created_at).toLocaleString()} · <span className={
                                    s.status === "approved" ? "text-emerald-400" : s.status === "rejected" ? "text-abnormal-red" : "text-amber-400"
                                }>{s.status}</span>
                            </p>
                            <p className="font-display text-2xl uppercase mt-1">{s.user_name} <span className="text-zinc-500">→</span> {s.trail_slug}</p>
                            <p className="text-xs text-zinc-400 mt-1">{s.user_email}</p>
                            <a href={s.strava_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-2 text-abnormal-red text-sm hover:underline break-all">
                                <ExternalLink size={14} /> {s.strava_url}
                            </a>
                            {s.note && <p className="mt-2 text-sm text-zinc-300 italic">{s.note}</p>}
                            {s.status === "rejected" && s.rejection_reason && <p className="mt-1 text-xs text-abnormal-red">Motivo: {s.rejection_reason}</p>}
                        </div>
                        {s.status === "pending" && (
                            <div className="flex flex-col gap-2 md:items-end">
                                <button onClick={() => approve(s.id)} disabled={busyId === s.id} className="bg-abnormal-red text-white text-xs uppercase tracking-wider font-bold px-4 py-2 inline-flex items-center gap-2 hover:bg-white hover:text-abnormal-red disabled:opacity-50">
                                    {busyId === s.id ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />} Approva
                                </button>
                                {rejectingId === s.id ? (
                                    <div className="flex flex-col gap-2 items-stretch md:items-end">
                                        <input
                                            value={rejectReason}
                                            onChange={(e) => setRejectReason(e.target.value)}
                                            placeholder="Motivo (opzionale)"
                                            className="bg-black border border-zinc-800 px-3 py-1.5 text-sm w-full md:w-64"
                                        />
                                        <div className="flex gap-2 justify-end">
                                            <button onClick={() => { setRejectingId(null); setRejectReason(""); }} className="text-xs uppercase border border-white/20 px-3 py-1.5">Annulla</button>
                                            <button onClick={() => reject(s.id)} disabled={busyId === s.id} className="text-xs uppercase bg-abnormal-red text-white px-3 py-1.5 font-bold">Conferma respingi</button>
                                        </div>
                                    </div>
                                ) : (
                                    <button onClick={() => setRejectingId(s.id)} className="text-xs uppercase tracking-wider font-bold px-4 py-2 border border-white/30 hover:border-abnormal-red hover:text-abnormal-red inline-flex items-center gap-2">
                                        <X size={14} /> Respingi
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
